// SoloSession — the thin, stateful driver the SoloMahjong panel renders against. It wraps the PURE,
// deterministic controller in soloGame.ts (newSolo / draw / discard / analysis) and keeps the
// current SoloState + its freshly-computed SoloAnalysis together so the React panel can stay
// presentational (one `forceRender` after each action). It introduces NO new randomness: all entropy
// flows through the integer seed into the engine RNG (via newSolo). Every action recomputes analysis
// from the new state, so the UI never reads stale assist data.
import type { RuleSet, ScoreResult, TileId, YakuDefMeta } from '@mj/protocol';
import { steppedUnits, manganUnitLabel } from '@mj/engine';
import {
  newSolo,
  draw as drawState,
  discard as discardState,
  analysis as analyze,
  type SoloState,
  type SoloAnalysis,
} from './soloGame';

export class SoloSession {
  private rs: RuleSet;
  private catalog: YakuDefMeta[] | undefined;
  /** Current controller state (13 between turns, 13 + drawn while holding a draw). */
  state: SoloState;
  /** Analysis of `state`, recomputed on every transition. */
  analysis: SoloAnalysis;
  /** The seed of the current deal (so the UI can echo "deal #N"). */
  seed: number;

  constructor(rs: RuleSet, seed: number, catalog?: YakuDefMeta[]) {
    this.rs = rs;
    this.catalog = catalog;
    this.seed = seed;
    this.state = newSolo(rs, seed, catalog);
    this.analysis = analyze(this.state);
  }

  private recompute() {
    this.analysis = analyze(this.state);
  }

  /** Re-deal from a fresh seed (the "New game" button). Uses the CURRENT ruleSet + catalog. */
  newGame(seed: number, rs?: RuleSet, catalog?: YakuDefMeta[]): void {
    if (rs) this.rs = rs;
    if (catalog) this.catalog = catalog;
    this.seed = seed;
    this.state = newSolo(this.rs, seed, this.catalog);
    this.recompute();
  }

  /** Apply the current RuleSet/catalog to scoring/assist WITHOUT re-dealing (save→reflect). */
  applyRules(rs: RuleSet, catalog?: YakuDefMeta[]): void {
    this.rs = rs;
    this.catalog = catalog;
    // Re-thread the rules through the live state so analysis (scoring) reflects the new ruleSet.
    this.state = { ...this.state, rs, ...(catalog ? { catalog } : {}) };
    this.recompute();
  }

  /** Draw the next wall tile (13 -> 13 + drawn = 14). Empty wall -> ryuukyoku. */
  draw(): void {
    this.state = drawState(this.state);
    this.recompute();
  }

  /** Discard a tile by full TileId (any of the 14), back to 13. */
  discardTile(tile: TileId): void {
    this.state = discardState(this.state, tile);
    this.recompute();
  }

  /** Test/setup hook: install a chosen holding (hand + optional drawn) and re-analyze. The wall is
   *  left as-is so wallCount stays meaningful; this is for pinning analysis, not normal play. */
  setHolding(hand: TileId[], drawn: TileId | null): void {
    this.state = { ...this.state, hand: [...hand].sort(), drawn };
    this.recompute();
  }
}

/** Factory mirroring the other Studio drivers (createStudioStore, etc.). */
export function createSession(rs: RuleSet, seed: number, catalog?: YakuDefMeta[]): SoloSession {
  return new SoloSession(rs, seed, catalog);
}

// ---------------------------------------------------------------------------
// cutHints — the SET of base TileIds the panel should visually highlight on the hand: the distinct
// discards that REACH/KEEP tenpai (analysis.tenpaiDiscards). Returned as a Set for O(1) lookup while
// rendering each tile. Empty when not holding 14 or no cut reaches tenpai.
// ---------------------------------------------------------------------------
export function cutHints(a: SoloAnalysis): Set<TileId> {
  return new Set(a.tenpaiDiscards.map((d) => d.discard));
}

// ---------------------------------------------------------------------------
// limitLabelFor — the Japanese mangan-class label for a finished ScoreResult, derived (like the
// engine's own limitLabel) from HAN / the yakuman multiplier — NOT from the capped point value.
// Returns '' for a sub-mangan hand. Aotenjou (uncapped) hands carry a han but no ceiling, so we still
// return the kazoe label when han crosses the ladder; the panel may choose to suppress it.
// ---------------------------------------------------------------------------
const YAKUMAN_PREFIX = ['', '', 'ダブル', 'トリプル', '四倍', '五倍', '六倍'];

export function limitLabelFor(r: ScoreResult, mode: 'capped' | 'aotenjou' | 'stepped' = 'capped'): string {
  if (r.yakuman > 0) {
    const prefix = YAKUMAN_PREFIX[r.yakuman] ?? `${r.yakuman}倍`;
    // A "kazoe yakuman" (han>=13 with no true yakuman yaku) reads 数え役満; a real yakuman reads 役満.
    const isKazoe = r.yaku.every((y) => y.han < 13) && r.han >= 13;
    if (isKazoe && r.yakuman === 1) return '数え役満';
    return `${prefix}役満`;
  }
  const han = r.han;
  // Stepped aotenjou: every 5+ han hand carries a mangan-unit label (満貫/跳満/倍満/三倍満/四倍満…).
  if (mode === 'stepped') return han >= 5 ? manganUnitLabel(steppedUnits(han)) : '';
  if (han >= 13) return '数え役満';
  if (han >= 11) return '三倍満';
  if (han >= 8) return '倍満';
  if (han >= 6) return '跳満';
  if (han === 5) return '満貫';
  // han 1-4: a limit hand only when the base was rounded/kiriage'd up to mangan-class points. We
  // can't see fu-rounding from the ScoreResult alone, so leave sub-5-han unlabelled (matches the
  // common display: only 5+ han / kiriage-mangan carry a name, which the engine flags via points).
  return '';
}
